import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { EnhancedSearch } from "../components/EnhancedSearch";
import { CopyButton } from "../components/CopyButton";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadPinCodes } from "../utils/dataLoader";
import type { PinCode } from "../types/data";
import { MapPin, Building2, Map } from "lucide-react";

export function PinCodesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedState = searchParams.get('state') || "";
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [visibleCount, setVisibleCount] = useState(40);
  const { data: pinCodes, loading, error } = useDataLoader(loadPinCodes);

  // Debounced search term for grid filtering to avoid lagging while typing
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(40);
  }, [debouncedSearch, selectedState]);

  // Get unique states for filtering
  const states = useMemo(() => {
    const source = pinCodes ?? [];
    const uniqueStates = Array.from(new Set(source.map(code => code.state || "")));
    return uniqueStates.filter(Boolean).sort();
  }, [pinCodes]);

  // Get unique cities and districts once when pinCodes load, to avoid heavy computation on search input changes
  const uniqueCitiesAndDistricts = useMemo(() => {
    const source = pinCodes ?? [];
    const cities = Array.from(new Set(source.map(code => code.city || "")));
    const districts = Array.from(new Set(source.map(code => code.district || "")));
    return [...cities, ...districts].filter(Boolean);
  }, [pinCodes]);

  // Get search suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];
    
    const allSuggestions = [...uniqueCitiesAndDistricts, ...states];
    const term = searchTerm.toLowerCase();
    
    return allSuggestions.filter(item => 
      item && item.toLowerCase().includes(term)
    ).slice(0, 8);
  }, [searchTerm, uniqueCitiesAndDistricts, states]);

  // Filter data
  const filteredPinCodes = useMemo(() => {
    let filtered: PinCode[] = (pinCodes ?? []);
    
    // Filter by search term
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (code) =>
          (code.pin && code.pin.includes(term)) ||
          (code.city && code.city.toLowerCase().includes(term)) ||
          (code.district && code.district.toLowerCase().includes(term)) ||
          (code.state && code.state.toLowerCase().includes(term))
      );
    }
    
    // Filter by state
    if (selectedState) {
      filtered = filtered.filter(code => code.state === selectedState);
    }
    
    return filtered;
  }, [debouncedSearch, selectedState, pinCodes]);

  // Display sliced results for pagination
  const displayedPinCodes = useMemo(() => {
    return filteredPinCodes.slice(0, visibleCount);
  }, [filteredPinCodes, visibleCount]);

  // State filters
  const stateFilters = useMemo(() => [
    { label: "All States", value: "", active: selectedState === "" },
    ...states.map(state => ({
      label: state,
      value: state,
      active: selectedState === state
    })).sort((a, b) => {
      const priorityStates = ["Maharashtra", "Delhi", "Karnataka", "Tamil Nadu", "Uttar Pradesh", "Rajasthan", "Gujarat", "West Bengal", "Bihar", "Madhya Pradesh"];
      const aIndex = priorityStates.indexOf(a.value);
      const bIndex = priorityStates.indexOf(b.value);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }
      return a.value.localeCompare(b.value);
    })
  ], [states, selectedState]);

  const handleFilterChange = useCallback((filterValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (filterValue) next.set('state', filterValue);
      else next.delete('state');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "Indian PIN Codes Database",
    "description": "Complete database of Indian postal codes (PIN codes) with city, district, and state information",
    "keywords": ["Indian PIN codes", "postal codes", "India post", "PIN code finder", "postal code search"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/pin-codes`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      {loading && (
        <div className="text-center py-4 text-gray-500">Loading PIN codes...</div>
      )}
      {error && (
        <div className="text-center py-4 text-red-600">Failed to load PIN codes. Please try again later.</div>
      )}
      <SEO
        {...routeSeo('pin-codes')}
        keywords="Indian PIN codes, postal codes, PIN code finder, India post codes, postal code search, PIN code database"
        structuredData={structuredData}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Indian PIN Codes Finder
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Search and find Indian postal codes (PIN codes) instantly. Copy PIN codes with one click 
            for all cities, districts, and states across India.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>{pinCodes?.length ?? 0} PIN Codes</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>{Array.from(new Set((pinCodes ?? []).map(c => c.city))).length} Cities</span>
            </div>
            <div className="flex items-center space-x-2">
              <Map className="h-4 w-4" />
              <span>{states.length} States</span>
            </div>
          </div>
        </div>

        {/* Enhanced Search */}
        <EnhancedSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search PIN codes, cities, districts, or states..."
          suggestions={suggestions}
          filters={stateFilters.slice(0, 10)}
          onFilterChange={handleFilterChange}
          resultsCount={filteredPinCodes.length}
        />

        {/* Quick State Filter Buttons */}
        {!searchTerm && (
          <div className="flex flex-wrap gap-2 mb-6 justify-center">
            <Button
              variant={selectedState === "" ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilterChange("")}
            >
              All States
            </Button>
            {states.slice(0, 8).map((state) => (
              <Button
                key={state}
                variant={selectedState === state ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterChange(state)}
              >
                {state}
              </Button>
            ))}
          </div>
        )}

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayedPinCodes.map((code) => (
            <Card key={code.pin} className="hover:shadow-lg transition-all duration-300 group">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between gap-2 min-w-0">
                  <span className="text-2xl font-bold text-blue-600 group-hover:text-blue-700 shrink-0">
                    {code.pin}
                  </span>
                  <div className="flex items-center space-x-2 min-w-0">
                    <Badge variant="secondary" className="text-xs truncate max-w-[100px] sm:max-w-[120px] shrink" title={code.state}>
                      {code.state}
                    </Badge>
                    <CopyButton text={code.pin} size="sm" className="shrink-0" />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">City</p>
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <p className="font-medium truncate max-w-[180px]" title={code.city}>{code.city}</p>
                      <CopyButton text={code.city} variant="ghost" size="sm" className="shrink-0" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">District</p>
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <p className="font-medium text-gray-700 truncate max-w-[180px]" title={code.district}>{code.district}</p>
                      <CopyButton text={code.district} variant="ghost" size="sm" className="shrink-0" />
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        const fullAddress = `${code.city}, ${code.district}, ${code.state} - ${code.pin}`;
                        navigator.clipboard.writeText(fullAddress);
                      }}
                    >
                      Copy Full Address
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Load More Button */}
        {filteredPinCodes.length > visibleCount && (
          <div className="flex justify-center mt-8">
            <Button
              onClick={() => setVisibleCount((prev) => prev + 40)}
              variant="outline"
              size="lg"
              className="px-8 shadow-sm"
            >
              Load More ({filteredPinCodes.length - visibleCount} remaining)
            </Button>
          </div>
        )}

        {/* No Results */}
        {filteredPinCodes.length === 0 && (
          <div className="text-center py-16">
            <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No PIN codes found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <Button variant="outline" onClick={() => { setSearchTerm(""); handleFilterChange(""); }}>
              Clear All Filters
            </Button>
          </div>
        )}

        {/* SEO Content Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About Indian PIN Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">What is a PIN Code?</h3>
              <p className="text-sm leading-relaxed">
                PIN (Postal Index Number) is a 6-digit code used by India Post to identify 
                post offices and facilitate efficient mail delivery across the country. 
                Each PIN code represents a specific geographic area.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">PIN Code Format</h3>
              <p className="text-sm leading-relaxed">
                The first digit represents the region, second digit the sub-region, 
                third digit the sorting district, and the last three digits identify 
                the specific post office within the district.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}